// TeacherDocuments.js
import React, { useEffect, useState } from 'react';
import API from './api';

function TeacherDocuments() {
  const [tab, setTab] = useState('general');

  const [generalDocs, setGeneralDocs] = useState([]);

  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentDocs, setStudentDocs] = useState([]);

  const [parents, setParents] = useState([]);
  const [selectedParentUserId, setSelectedParentUserId] = useState('');
  const [parentDocs, setParentDocs] = useState([]);

  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchGeneral();
    fetchStudents();
    fetchParents();
  }, []);

  const fetchGeneral = async () => {
    const res = await API.get('/documents/general');
    setGeneralDocs(res.data || []);
  };

  const fetchStudents = async () => {
    const res = await API.get('/students?assigned_only=1');
    setStudents(res.data || []);
  };

  const fetchStudentDocs = async id => {
    if (!id) return setStudentDocs([]);
    const res = await API.get(`/documents/student/${id}`);
    setStudentDocs(res.data || []);
  };

  const fetchParents = async () => {
    try {
      const res = await API.get('/document-parents/assigned');
      setParents(res.data || []);
    } catch {
      setParents([]);
    }
  };

  const fetchParentDocs = async parentUserId => {
    if (!parentUserId) return setParentDocs([]);
    const res = await API.get(`/documents/parent/${parentUserId}`);
    setParentDocs(res.data || []);
  };

  const handleDownload = async (docId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/documents/download/${docId}`, {
        headers: { Authorization: token }
      });
      if (!res.ok) throw new Error('Failed to download file');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download failed!');
    }
  };

  const fmtDate = v => (v ? String(v).replace('T', ' ').slice(0, 16) : '');

  const handleStudentUpload = async e => {
    e.preventDefault();
    if (!selectedStudent) return alert('Select student!');
    setUploading(true);
    const form = new FormData(e.target);
    try {
      await API.post(`/documents/student/${selectedStudent}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      e.target.reset();
      fetchStudentDocs(selectedStudent);
    } finally {
      setUploading(false);
    }
  };

  const handleParentUpload = async e => {
    e.preventDefault();
    if (!selectedParentUserId) return alert('Select parent!');
    setUploading(true);
    const form = new FormData(e.target);
    try {
      await API.post(`/documents/parent/${selectedParentUserId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      e.target.reset();
      fetchParentDocs(selectedParentUserId);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-2 md:p-6 max-w-2xl mx-auto">
      <h2 className="text-lg font-bold mb-3 text-center">Documents</h2>

      <div className="flex gap-2 justify-center mb-4 text-sm">
        <button
          onClick={() => setTab('general')}
          className={`px-4 py-2 rounded-full transition ${
            tab === 'general'
              ? 'bg-blue-600 text-white shadow'
              : 'bg-gray-100 hover:bg-gray-200'
          }`}
        >
          General
        </button>
        <button
          onClick={() => setTab('student')}
          className={`px-4 py-2 rounded-full transition ${
            tab === 'student'
              ? 'bg-blue-600 text-white shadow'
              : 'bg-gray-100 hover:bg-gray-200'
          }`}
        >
          Upload for student
        </button>
        <button
          onClick={() => setTab('parent')}
          className={`px-4 py-2 rounded-full transition ${
            tab === 'parent'
              ? 'bg-blue-600 text-white shadow'
              : 'bg-gray-100 hover:bg-gray-200'
          }`}
        >
          Upload for parent
        </button>
      </div>

      {/* GENERAL */}
      {tab === 'general' && (
        <div className="bg-white rounded-lg p-3 shadow mb-6">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-300 rounded">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2">Type</th>
                  <th className="p-2">File</th>
                  <th className="p-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {generalDocs.map(doc => (
                  <tr key={doc.id} className="border-t">
                    <td className="p-2">{doc.doc_type}</td>
                    <td className="p-2">
                      <button
                        onClick={() =>
                          handleDownload(doc.id, doc.file_path.split('/').pop())
                        }
                        className="text-blue-700 underline font-medium"
                      >
                        Download
                      </button>
                    </td>
                    <td className="p-2">{fmtDate(doc.uploaded_at)}</td>
                  </tr>
                ))}
                {generalDocs.length === 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center text-gray-400 py-4"
                    >
                      No general documents
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* STUDENT */}
      {tab === 'student' && (
        <div className="bg-white rounded-lg p-3 shadow">
          <select
            value={selectedStudent}
            onChange={e => {
              setSelectedStudent(e.target.value);
              fetchStudentDocs(e.target.value);
            }}
            className="border p-2 rounded mb-3 w-full text-sm"
          >
            <option value="">-- Select Student --</option>
            {students.map(s => (
              <option key={s.id} value={s.id}>
                {s.first_name} {s.last_name}
              </option>
            ))}
          </select>

          {selectedStudent && (
            <>
              <form
                onSubmit={handleStudentUpload}
                className="flex flex-col sm:flex-row gap-2 items-center my-2 text-sm"
              >
                <input
                  type="file"
                  name="file"
                  required
                  className="border border-gray-300 rounded px-2 py-1 flex-1 text-sm"
                />
                <input
                  type="text"
                  name="doc_type"
                  placeholder="Document type"
                  required
                  className="border border-gray-300 rounded px-2 py-1 flex-1 text-sm"
                />
                <button
                  type="submit"
                  disabled={uploading}
                  className={`px-4 py-1 rounded-full bg-blue-500 text-white font-medium transition hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm ${
                    uploading ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </form>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border border-gray-300 rounded">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2">Type</th>
                      <th className="p-2">File</th>
                      <th className="p-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentDocs.map(doc => (
                      <tr key={doc.id} className="border-t">
                        <td className="p-2">{doc.doc_type}</td>
                        <td className="p-2">
                          <button
                            onClick={() =>
                              handleDownload(
                                doc.id,
                                doc.file_path.split('/').pop()
                              )
                            }
                            className="text-blue-700 underline font-medium"
                          >
                            Download
                          </button>
                        </td>
                        <td className="p-2">{fmtDate(doc.uploaded_at)}</td>
                      </tr>
                    ))}
                    {studentDocs.length === 0 && (
                      <tr>
                        <td
                          colSpan={3}
                          className="text-center text-gray-400 py-4"
                        >
                          No documents for student
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* PARENT */}
      {tab === 'parent' && (
        <div className="bg-white rounded-lg p-3 shadow">
          <select
            value={selectedParentUserId}
            onChange={e => {
              setSelectedParentUserId(e.target.value);
              fetchParentDocs(e.target.value);
            }}
            className="border p-2 rounded mb-3 w-full text-sm"
          >
            <option value="">-- Select Parent --</option>
            {parents.map(p => (
              <option key={p.user_id} value={p.user_id}>
                {(p.last_name || '') + ', ' + (p.first_name || '')} ({p.email})
              </option>
            ))}
          </select>

          {selectedParentUserId && (
            <>
              <form
                onSubmit={handleParentUpload}
                className="flex flex-col sm:flex-row gap-2 items-center my-2 text-sm"
              >
                <input
                  type="file"
                  name="file"
                  required
                  className="border border-gray-300 rounded px-2 py-1 flex-1 text-sm"
                />
                <input
                  type="text"
                  name="doc_type"
                  placeholder="Document type"
                  required
                  className="border border-gray-300 rounded px-2 py-1 flex-1 text-sm"
                />
                <button
                  type="submit"
                  disabled={uploading}
                  className={`px-4 py-1 rounded-full bg-blue-500 text-white font-medium transition hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm ${
                    uploading ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </form>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border border-gray-300 rounded">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2">Type</th>
                      <th className="p-2">File</th>
                      <th className="p-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parentDocs.map(doc => (
                      <tr key={doc.id} className="border-t">
                        <td className="p-2">{doc.doc_type}</td>
                        <td className="p-2">
                          <button
                            onClick={() =>
                              handleDownload(
                                doc.id,
                                doc.file_path.split('/').pop()
                              )
                            }
                            className="text-blue-700 underline font-medium"
                          >
                            Download
                          </button>
                        </td>
                        <td className="p-2">{fmtDate(doc.uploaded_at)}</td>
                      </tr>
                    ))}
                    {parentDocs.length === 0 && (
                      <tr>
                        <td
                          colSpan={3}
                          className="text-center text-gray-400 py-4"
                        >
                          No documents for parent
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default TeacherDocuments;




